/**
 * Close code for a socket that has been superseded by a newer one on the same
 * session token — a reload, or a second tab.
 *
 * It has to be distinguishable from a network drop. A superseded client that
 * reconnects on a timer takes the seat straight back and closes the socket that
 * displaced it, which reconnects in turn: two tabs on one browser share a token
 * and so fought forever, each showing "connection lost" every couple of
 * seconds. The displaced tab must sit still instead.
 *
 * 4000-4999 is the range reserved for application use.
 */
export const WS_SUPERSEDED = 4001;
import { alliesFromSide, resolveSide } from './sideWire.js';
import { stanceFor } from './types.js';
// --- client -> server ------------------------------------------------------
/**
 * The wire format this build speaks, named in `hello`. Raised whenever a
 * change would break a client from before it. A tab left open across a
 * deploy runs the old client against the new server: one that names an older
 * protocol (or, from before this number, a `wire` capability list) is told to
 * reload rather than left to misread the board.
 *
 *   3  9 October 2026: phase reports only with news, plan lines once,
 *      commits acknowledged by ref, the stance and launcher reach rebuilt on
 *      arrival, about fifty unread fields gone (docs/archive/server/DATA_SERVED.md)
 *   5  16 October 2026: one chat channel for the whole side, each line
 *      stamped with where its sender stood
 *   6  16 October 2026: chat lines and `say` carry no channel, and lines no
 *      sender cluster or region; an older client filtered on them and would
 *      show an empty chat
 *   7  18 October 2026: every spend of the round comes out of the city's own
 *      allocation; `allocation` replaces `attackFuel`, and the treasury loses
 *      its spending order and reserved fuel and gains what is smuggled in
 *   8  18 October 2026: the rules a client applies from `welcome` give each
 *      weapon a place in its wave (`arrival`, replacing its speed) and each gun
 *      a hold-back table (HOLD_BACK, replacing HOLD_FOR_HIGH), and the merge
 *      choice is 'arrival'; an older client refuses the whole set and plays on
 *      its own numbers. A defender's damage records name the launchers it lost
 *   9  18 October 2026: clusters are 4v4 and the war 64 a side; `battle`
 *      opens a one-cluster match against NPCs or joins the next one against
 *      other commanders, the lobby lists a battle's open call-up (`battles`),
 *      and a match's config says when it is a battle (`cluster`)
 *  10  19 October 2026: the elastic establishment (D19). The rules a client
 *      applies from `welcome` gain ELASTIC_ESTABLISHMENT_PER_MISSING_CITY, which
 *      an older client refuses whole; a city carries its `elastic` places and
 *      `inactive` launchers, a Plan takes RECOMMISSION, and a Side HQ row its
 *      `establishment` when it is not the usual one
 *  11  19 October 2026: the elastic establishment cuts the side ahead (D20).
 *      The rules gain ESTABLISHMENT_CUT_PER_CITY_AHEAD and ESTABLISHMENT_MIN,
 *      which an older client refuses whole; a city's `elastic.bonus` may be
 *      negative, and its own city carries `culled`, the launchers scrapped at
 *      the last round's end
 *  12  20 October 2026: a commander chooses a side (D23). `play`, `train` and
 *      `battle` carry `side`, a war summary its `sides`, and a full side is
 *      refused by name (SIDE_FULL)
 *  13  20 October 2026: Plan orders are built as the exchange begins (D24).
 *      The own view carries `onOrder` through Attack and Defence, what the
 *      plan will turn out; a client that read its new stock and reserve in
 *      Attack would otherwise show orders it cannot yet use. The rules gain
 *      DOGLEG_FUEL_PER_ROUND (D26), which an older client refuses whole
 *  14  21 October 2026: the Master NPC (D27). A player's `bot` and a Side HQ
 *      city's may be MASTER, which an older client has no label for: its
 *      Side HQ row would fail on the first Master on its own side
 *  15  24 October 2026: the access switch (D36). The lobby message carries
 *      `access`, `train` and `battle` a `password`, and a closed game refuses
 *      with GAME_CLOSED, which an older client would show as a bare notice
 *      with no way to give the tester password
 *  16  25 October 2026: sound switched from the simulation room (D37).
 *      `welcome` carries `sound` and a `sound` message changes it; an older
 *      client would ignore both and go on playing sound the owner turned off
 */
export const PROTOCOL_VERSION = 16;
/** A list rebuilt from runs over the one held, or null if a run reaches past it. */
function expandRuns(held, runs) {
    const out = [];
    for (const r of runs) {
        if (Array.isArray(r)) {
            const [from, count] = r;
            if (from < 0 || count < 0 || from + count > held.length)
                return null;
            for (let i = 0; i < count; i++)
                out.push(held[from + i]);
        }
        else
            out.push(r);
    }
    return out;
}
export function newHeldBoard() {
    return { cities: new Map(), layouts: new Map(), keys: new Map(), view: null, side: null, players: null };
}
/** A packed list made whole, or null if it names a layout this client does not hold. */
function unpack(list, held) {
    if (Array.isArray(list))
        return list;
    const p = list;
    let layout = p.layout ?? held.layouts.get(p.key);
    if (!layout && p.base && p.runs) {
        const base = held.layouts.get(p.base);
        layout = base ? expandRuns(base, p.runs) ?? undefined : undefined;
        if (layout)
            held.layouts.set(p.key, layout);
    }
    if (!layout)
        return null;
    if (p.layout)
        held.layouts.set(p.key, p.layout);
    return p.state ? layout.map((item, i) => ({ ...item, ...p.state[i] })) : layout;
}
/** A city with its packed lists made whole, or null. */
function unpackCity(city, held) {
    if (Array.isArray(city.urban) && Array.isArray(city.trees))
        return city;
    const urban = unpack(city.urban, held);
    const trees = unpack(city.trees, held);
    return urban && trees ? { ...city, urban, trees } : null;
}
/** The layout key a packed list was sent on, if it was packed. */
function keyOf(list) {
    return list && !Array.isArray(list) ? list.key : undefined;
}
/**
 * The held copy of an enemy city brought up to date (HeldCity's set, at and
 * gone), or null if it cannot be: no copy held, or a change to a list this
 * client does not hold the layout of.
 */
function patchCity(e, held, known = held.cities.get(e.id), 
/** Where its layout keys are kept: by id for an enemy, apart for the commander's own city. */
keyId = e.id) {
    if (!known)
        return null;
    if (!e.set && !e.at && !e.seq && !e.gone)
        return known;
    const city = { ...known };
    const keys = { ...held.keys.get(keyId) };
    for (const [k, v] of Object.entries(e.set ?? {})) {
        if (k === 'urban' || k === 'trees') {
            const list = unpack(v, held);
            if (!list)
                return null;
            city[k] = list;
            keys[k] = keyOf(v);
        }
        else
            city[k] = v;
    }
    for (const [k, changes] of Object.entries(e.at ?? {})) {
        const list = [...city[k]];
        const packed = k === 'urban' || k === 'trees';
        const layout = packed ? held.layouts.get(keys[k] ?? '') : null;
        if (packed && !layout)
            return null;
        for (const [i, item] of changes) {
            if (i < 0 || i >= list.length)
                return null;
            list[i] = packed ? { ...layout[i], ...item } : item;
        }
        city[k] = list;
    }
    for (const [k, r] of Object.entries(e.seq ?? {})) {
        const list = expandRuns(city[k], r);
        if (!list)
            return null;
        city[k] = list;
    }
    for (const k of e.gone ?? [])
        delete city[k];
    held.keys.set(keyId, keys);
    return city;
}
/**
 * A view off the wire, made whole from what this client holds — enemy cities
 * and layouts — and what it was just sent kept for the next. Null when it names
 * a city or a layout the client does not hold, which only a client that has
 * lost track can see: it then asks for a whole view (`resync`). `held` belongs
 * to one match, and is emptied whenever the client joins or leaves one, as the
 * server's record of it is.
 */
/** Where the commander's own city's layout keys are kept: the same city may also be someone's enemy. */
const OWN = '\u0000own';
export function resolveView(view, held) {
    let city;
    if ('held' in view.city) {
        // The own city goes as what changed since the last view (keys under its id, as an enemy's would be).
        const last = held.view?.city;
        city = last && last.id === view.city.id
            ? patchCity(view.city, held, last, OWN)
            : null;
    }
    else {
        city = unpackCity(view.city, held);
        if (city)
            held.keys.set(OWN, { urban: keyOf(view.city.urban), trees: keyOf(view.city.trees) });
    }
    if (!city)
        return null;
    const parts = {};
    for (const k of view.same ?? []) {
        if (!held.view || !(k in held.view))
            return null;
        parts[k] = held.view[k];
    }
    const enemies = [];
    for (const e of view.enemies) {
        if ('held' in e) {
            const known = patchCity(e, held);
            if (!known)
                return null;
            enemies.push(known);
        }
        else {
            const whole = unpackCity(e, held);
            if (!whole)
                return null;
            held.keys.set(e.id, { urban: keyOf(e.urban), trees: keyOf(e.trees) });
            enemies.push(whole);
        }
    }
    for (const e of enemies)
        held.cities.set(e.id, e);
    const { same: _same, shared: _shared, ...rest } = view;
    const players = Array.isArray(view.match.players) ? view.match.players : held.view?.match.id === view.match.id ? held.players : null;
    if (!players)
        return null;
    let side = view.side;
    if (view.side && 'scope' in view.side) {
        const next = resolveSide(view.side, view.match.id, held.side);
        if (!next)
            return null;
        held.side = next;
        side = next.value;
    }
    const whole = { ...rest, ...parts, match: { ...view.match, players, stance: stanceFor(view.match.phase) }, city, enemies,
        ...(side ? { side } : {}) };
    if (view.shared?.includes('allies')) {
        if (!side || side.faction !== whole.you.faction)
            return null;
        whole.allies = alliesFromSide(side, whole);
    }
    if (view.shared?.includes('incoming')) {
        if (!side?.incoming)
            return null;
        whole.incoming = side.incoming;
    }
    held.players = players;
    held.view = whole;
    return whole;
}
/**
 * A small state message applied to the view a client already holds.
 *
 * The server sends a whole view when the board changes, and one of these when
 * only a line of it has: a commit, the replay hold, the clock, the roster, the
 * side from above. Null when the message is not one of them, or belongs to a
 * match other than the one this view is of, so a stale room is never revived
 * by a late delta. The client and the end-to-end tests both patch through
 * here, so they cannot drift apart.
 */
export function patchView(view, msg, held) {
    switch (msg.t) {
        case 'commit': {
            if (view.match.id !== msg.matchId)
                return null;
            // An acknowledgement by `ref` carries no orders: the client that sent
            // them puts its own copy in `commitment` before patching (net.ts).
            return msg.commitment !== undefined
                ? { ...view, committed: msg.committed, yourCommitment: msg.commitment }
                : { ...view, committed: msg.committed };
        }
        case 'replay': {
            if (view.match.id !== msg.matchId)
                return null;
            return { ...view, match: { ...view.match, replayHold: { waiting: msg.waiting, total: msg.total } } };
        }
        case 'clock':
            if (view.match.id !== msg.matchId)
                return null;
            return { ...view, match: { ...view.match, phaseEndsAt: msg.phaseEndsAt, serverNow: msg.serverNow, replayHold: msg.replayHold } };
        case 'roster': {
            if (view.match.id !== msg.matchId)
                return null;
            if (held)
                held.players = msg.players;
            const next = msg.renamed ? renameCommander(view, msg.renamed.from, msg.renamed.to) : view;
            return {
                ...next,
                match: { ...next.match, players: msg.players, host: msg.host, youAreHost: msg.host != null && msg.host === view.you.id },
            };
        }
        case 'side': {
            if (view.match.id !== msg.matchId)
                return null;
            const next = resolveSide(msg.side, msg.matchId, held?.side ?? null);
            if (!next || !held)
                return null;
            held.side = next;
            const side = next.value;
            return { ...view, side,
                ...(side.faction === view.you.faction ? { allies: alliesFromSide(side, view),
                    ...(view.match.phase === 'DEFEND' && side.incoming ? { incoming: side.incoming } : {}) } : {}),
            };
        }
        case 'signals': {
            if (view.match.id !== msg.matchId)
                return null;
            if (view.city.id !== msg.cityId && !view.allies.some((a) => a.id === msg.cityId))
                return null;
            const allies = view.allies.map((a) => a.id === msg.cityId ? { ...a, signals: msg.signals } : a);
            const side = view.side ? { ...view.side, cities: view.side.cities.map((c) => c.id === msg.cityId ? { ...c, signals: msg.signals } : c) } : undefined;
            if (held?.side && held.side.matchId === msg.matchId)
                held.side = { ...held.side,
                    value: { ...held.side.value, cities: held.side.value.cities.map((c) => c.id === msg.cityId ? { ...c, signals: msg.signals } : c) } };
            if (held?.view)
                held.view = { ...held.view, allies,
                    ...(held.view.city.id === msg.cityId ? { city: { ...held.view.city, signals: msg.signals } } : {}),
                };
            return { ...view, allies, ...(side ? { side } : {}),
                ...(view.city.id === msg.cityId ? { city: { ...view.city, signals: msg.signals } } : {}),
            };
        }
        default:
            return null;
    }
}
/**
 * A view with one commander's id swapped for another's wherever it names
 * them: a seat in the war changing hands. Their city, and everything known of
 * it, stays as it was.
 */
function renameCommander(view, from, to) {
    const id = (x) => (x === from ? to : x);
    const owned = (xs) => (xs.some((x) => x.owner === from) ? xs.map((x) => (x.owner === from ? { ...x, owner: to } : x)) : xs);
    return {
        ...view,
        allies: owned(view.allies),
        enemies: owned(view.enemies),
        ...(view.spectating ? { spectating: { ...view.spectating, of: id(view.spectating.of) } } : {}),
        ...(view.side ? { side: { ...view.side, cities: owned(view.side.cities) } } : {}),
    };
}
export function encode(msg) {
    return JSON.stringify(msg);
}
export function decode(raw) {
    try {
        return JSON.parse(raw);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=protocol.js.map